// Every tunable number in one place. The in-game wiki prints these directly.
export const CONFIG = {
  tickMs: 200,
  catchWindowMs: 15_000,
  throwMs: 1200,
  spawnDelayMs: [800, 2000] as [number, number],
  maxWildsOnField: 3,
  /**
   * Wild HP and attack against an owned creature of the same level (battle design v2, 3.7, with moves):
   * an at-level lead takes about 10 blows a wild, about 350 kills an hour (the reference game's pace), and
   * potion use per hour stays near what it was at the old, faster pace (wilds hit softer for longer).
   * 2.8 -> 2.72 on 2026-10-15 when crits went (battle consistency), and back to 2.8 the same day when the retune
   * brought hunt crits back (6% at ×1.5, about 3% on the average blow): at 2.72 the at-level pace ran over its band
   * (sim/TUNING_LOG.md). 2.8 -> 3.0 on 2026-10-17: a grinder reached level 150 at 30.9 h, under the reference's 32-45 h.
   */
  wildHpMult: 3.0,
  wildAtkMult: 0.06,
  /** Base attack gap in ms (scaled down by Swift). */
  attackMs: 1500,
  walkTilesPerSec: 5,
  fieldW: 20,
  fieldH: 11,
  teamSize: 5,
  allFaintedRestMs: 12_000,
  /** This many wipes on one hunt within wipeWindowMs moves the trainer to an easier hunt. */
  wipeRetreatCount: 3,
  wipeWindowMs: 15 * 60_000,
  /**
   * A wipe with no Revive and no potion in the bag ends the hunt: the team goes back to Kinhall and waits there
   * until the player picks a hunt (pokeidle's rule; Cole, 2026-10-15). With a potion Auto potion would drink, it rests and returns.
   */
  wipeStopsWhenEmpty: true,
  autoPotionBelow: 0.4,
  /** Odds are 1-in-n unless stated. */
  /** Aura odds of an egg's hatch roll (shared/eggs.ts). */
  auraOneIn: 24_000,
  /**
   * Aura odds of a successful hunt catch: the wild looks plain and the catch reveals the Aura (rollCatchAura in
   * shared/rolls.ts). pokeidle rolls its shiny 1 in 24,000 per catch; ours is a little better because the old
   * spawn roll threw the strongest seal at every Aura, even with Throw at off, and players who throw at fewer
   * wilds now see fewer. At 22,000 active bots get 0.98-1.08× their old Aura supply and the whole sim server
   * 0.93× (sim/targets.ts auraVsOldAll; mythic-idle-design/catch-and-aura.md).
   */
  auraCatchOneIn: 22_000,
  /**
   * Aura Charm (shared/auracharm.ts): `hours` of Aura odds ×`oddsMult` on the trainer's hunt catches. Sold in the
   * diamond shop for `price` (provisional) and given free by admins and rewards. More time extends it; the odds
   * never go past ×oddsMult however many run at once.
   */
  // forSale false: not sold for diamonds (no paid odds); still given free by quests, the pass and admin. Cole decides its use later.
  auraCharm: { price: 25, hours: 24, oddsMult: 2, forSale: false as boolean },
  // Bloodline is the 1-to-5 power tier. pct = chance in percent (sums to 100).
  bloodline: [
    { name: 'I', mult: 1, pct: 74.495 },
    { name: 'II', mult: 1.05, pct: 20 },
    { name: 'III', mult: 1.1, pct: 5 },
    { name: 'IV', mult: 1.25, pct: 0.5 },
    { name: 'V', mult: 2, pct: 0.005 },
  ],
  /**
   * Rating points (of 100), relative to the species: a perfect roll rates the
   * same on a Common as on an Epic, and the Epic simply has better stats.
   * Talent and quality up to 20 each, bloodline by rarity rather than tier
   * number, aura the single biggest roll.
   */
  ratingPoints: { talent: 20, quality: 20, aura: 30 },
  bloodlineRating: [0, 4, 10, 20, 30],
  /** Each ascension stage adds this many rating points, so ascending never lowers it. */
  ratingPerStage: 1,
  /** Quality multiplier: average of three uniform draws, so extremes are rare. */
  quality: { min: 0.8, max: 1.2, draws: 3 },
  auraMult: 3,
  starMult: 0.1,
  maxStarsByRegion: { midgard: 3, hellas: 5, yomi: 7 } as Record<string, number>,
  rareTraitOneIn: 500,
  spawnWeight: { common: 60, uncommon: 30, rare: 12, epic: 4, legendary: 0 } as Record<string, number>,
  /** No real cap: creatures climb the same curve as trainers. */
  creatureLevelCap: 100_000,
  /**
   * Shrine Shards drop only on hunts at or above this level (Hellas), 1 in n kills before drop bonuses.
   * 20,000 -> 50,000 on 2026-10-20: the 1,000-trainer market sim made 2.5x the shards it used (5,270 vs 2,074),
   * selling near 2,300 coins; a hard grinder's first shrine moves from about 10 days to 3-4 weeks.
   */
  shrineShardMinLevel: 150,
  shrineShardOneIn: 50_000,
  /**
   * Scales all kill XP (shared/xp.ts killXp). Calibrated on real reference-game
   * data (sim/README.md, "Pace calibration"): its XP curve is the same as ours,
   * but an optimised player there reaches level 100 in about 15 h with ×2 XP
   * boosts, about 24 h at our VIP's ×1.25. 0.45 → 0.50 with the battle design's
   * slower at-level hunts (v2, 3.7): players farm a little below their level,
   * where the level edge keeps kills fast, so XP per hour barely moves.
   */
  killXpMult: 0.5,
  /** VIP multiplies kill XP for trainer and creatures. */
  vipXpMult: 1.25,
  /**
   * Away hunting (shared/offline.ts; Cole, 2026-10-20): after the game closes, the team keeps hunting at `pace` of
   * what it actually earned over its last `windowMs` of hunting: kill coins, trainer XP and creature XP, nothing
   * else. Only after `minHuntMs` of hunting (a short visit after it carries it on) and with the hunt running at the end. At most
   * `maxAwayPerDayMs` of time away counts per UTC day; an absence under `minAwayMs` gets nothing (a deploy's
   * reconnect). Credited at the next log-in. sim/economy.ts projects it (--no-offline for the baseline): on 2026-10-20
   * the 30+ day median level at day 90 went 709 → 795 (band 500-800), coins per active player ×1.14, rare catches as before.
   */
  offline: { pace: 0.25, maxAwayPerDayMs: 6 * 3_600_000, minHuntMs: 30 * 60_000, windowMs: 60 * 60_000, minAwayMs: 5 * 60_000 },
  /** Market sale fee, burned (shared/market.ts). The reference game raised its coin fee from 10% to 15%. */
  marketFeeCoins: 0.15,
  marketHoldMs: 120_000,
  marketListingDays: 7,
  /** Each spawn has a 1-in-n chance to be a visitor from elsewhere in the region (weighted by rarity). */
  visitorOneIn: 40,
  /**
   * Boss keys drop 1 in n kills on each region's hunts from tokenFrom to tokenTo (grounds.ts). The
   * reference game's token is 1 in 2,000 in its first Outland, a side zone
   * that costs XP to farm; ours drop on the main climb, so half that rate.
   * An active player finds a few a week (sim/targets.ts: keysFoundPerWeek).
   */
  bossTokenOneIn: 4_000,
  /**
   * A boss win binds the boss itself (a tradeable legendary) at `chance`, but
   * only the first `rollsPerWeek` wins against each boss in a UTC week roll
   * for it; later wins still pay coins, star items and relics. Keeps boss
   * legendaries about as scarce as Bloodline V catches (sim/targets.ts
   * legendaryPerBl4All, and legendaryPerBl4 on the 90-day projection).
   * 5% → 3.5% (2026-10-20 economy audit): the projection had 1.35 boss
   * legendaries per 100 Bloodline IV catches, over the 1 that BL V gets.
   */
  bossLegendary: { chance: 0.035, rollsPerWeek: 3 },
  /**
   * Held items (shared/held.ts): a piece of an item from the boss's pool drops on this share of boss wins (after that
   * boss's Trail first clear) and Boss Ladder wins. Ten pieces of one item forge it and the pool has four, so a copy
   * from boss wins alone is a long grind: about fifty wins with Market trades, far more without (pacing in
   * mythic-idle-design/trail-and-held-items.md; sim/eval.ts heldPiecesPerWeek).
   */
  held: { pieceChance: { boss: 0.2, ladder: 0.2 } },
  /** The Trail (shared/trail.ts): a boss node's first clear pays this many diamonds once (6 over the three bosses; was 5 each before 2026-10-20). */
  trail: { bossDiamonds: 2 },
  /**
   * Relics (shared/items.ts): a kill on a Lv 15+ hunt drops one 1 in this many, times drop perks. 400 -> 2,000 on
   * 2026-10-20 with rarer top rolls (RELIC_QUALITY): at 400 an active player found about 93 a week, and a 1,000-player
   * server minted about 5,400 Q95+ relics a week; now about 19 and 390 (sim/TUNING_LOG.md).
   */
  relicOneIn: 2000,
  /**
   * Ascension stones (shared/items.ts): a kill drops one 1 in `oneIn`, of one of the wild's own elements, or of any
   * element `leak` of the time (pokeidle: a pokémon drops its own type's stone, ~20% leaking to other types). The
   * reference drops 0.3-0.6% per kill from typed species and burns them in refine (500 × N³); ours have no refine
   * sink, only ascension, so they drop rarer: about one every 1-2 h of active hunting at 1,000 kills/h, each a
   * specific element, so most of them are bought and sold. A starter takes its own stone like any creature, so
   * while it still needs one (and the bag has none) a kill also drops that stone 1 in `starterOneIn`, for at most
   * `starterMax` stones a trainer: both of the starter's ascensions land on day one, whatever is hunted.
   */
  stones: { oneIn: 1_500, leak: 0.2, starterOneIn: 150, starterMax: 2 },
  starMaterialOneIn: 250,
  /**
   * Daily login reward diamonds over a 7-day streak (4 a week). Every day also
   * pays 10 Rune Seals, so a 0-diamond day still rewards the streak. Cut from
   * 1,1,2,2,2,3,5 (16 a week) on 2026-10-14 to keep free diamonds low
   * (docs: diamond-economy-review).
   */
  dailyClaimDiamonds: [0, 0, 1, 0, 0, 0, 2] as readonly number[],
  /**
   * A trainer level-up pays this many kills' worth of coins at the new level's
   * hunt (shared/xp.ts levelUpCoins). Levels are for opening regions, hunts and
   * features, so the reward stays a small share of hunting income (eval band
   * levelUpCoinShare). Level-ups pay no diamonds: those come from the daily
   * claim, quests, Bestiary milestones and arena and Guild War prizes.
   */
  levelUpCoinKills: 3,
  xpBoostMult: 1.5,
  coinBoostMult: 1.5,
  /**
   * Paid boosts (shared/boosts.ts), bought with diamonds (about $0.08 each). Three kinds, each sold in the
   * same five lengths; buying the same kind again adds time, different kinds multiply. Effects: trainer
   * kill XP, creature kill XP (the lead and every share of its XP), and Loot: kill coins, never any drop
   * or any odds. The reference sells five kinds
   * in eight lengths from 1 h to 30 d (pokeidle 03-hunts-xp-progression.md §7, 04-economy §8.2: XP and
   * creature XP ×1.5, Loot +40% drops; its capture and shiny boosts are odds, which diamonds never buy here).
   * Its grid is not in source; the one price on record is "XP boosts from 3 diamonds an hour" (pokeidle
   * HANDOFF-us-version.md), and its diamond is worth about ours. So an hour is 3, and the per-hour price
   * falls with length (3, 1.7, 1.0, 0.45, 0.21): 30 days of one kind costs 150, 1.5 season passes.
   * The old 1-hour XP and Coin boost offers (3 each) are folded into these.
   *
   * NOTE (Cole, 2026-10-14): he wants Loot to raise every drop rate if a lawyer later approves it. Not done:
   * today it must never raise a tradeable drop (ascension and star items, shards, keys, eggs, tablets, relics).
   */
  paidBoosts: {
    mult: { txp: 1.5, cxp: 1.5, loot: 1.5 },
    tiers: [
      { hours: 1, price: 3 },
      { hours: 6, price: 12 },
      { hours: 24, price: 35 },
      { hours: 168, price: 120 },
      { hours: 720, price: 300 },
    ],
    /** All three kinds at once (shop thread, 2026-10-15): about a fifth off buying them one by one; 1 h and 6 h are not bundled. */
    bundle: [
      { hours: 24, price: 85 },
      { hours: 168, price: 290 },
      { hours: 720, price: 720 },
    ],
  },
  /**
   * Late Joiner boost (shared/latejoiner.ts): kill XP ×mult on trainer and creatures until the trainer
   * reaches the median level of players active in the last activeDays days whose accounts are
   * veteranDays+ old (admin and dev left out); not sold under minPlayers of them. Price
   * max(minPrice, ceil(fullPrice × XP still missing / target XP)). Reference: pokeidle
   * 03-hunts-xp-progression.md §8 (the same numbers).
   */
  lateJoiner: { mult: 10, minPlayers: 20, activeDays: 7, veteranDays: 30, fullPrice: 1000, minPrice: 50 },
  // Hunt field (maps come from shared/worldmap.ts)
  /** Wilds arrive in waves; the next wave walks in this long after the last one falls. */
  /** The lead pauses this long after a kill before chasing the next wild (the reference game's re-target time). */
  afterKillMs: 800,
  waveSize: 15,
  wavePauseMs: 1500,
  wildWalkTilesPerSec: 1.6,
  wildRoamMs: [2500, 7000] as [number, number],
} as const;

/**
 * How long a player stays loaded after the last connection closes (server/world.ts): the hunt keeps going, and a
 * refresh, a wifi blip or a stalled socket that reconnects in time picks up the same session. Past it the session ends.
 */
export const DISCONNECT_GRACE_MS = 90_000;
